import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import {rxResource, takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {CurrencyPipe, DatePipe, JsonPipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {catchError, firstValueFrom, last, of, switchMap, takeWhile, timer} from 'rxjs';
import {ConfirmationService, MessageService, PrimeTemplate} from 'primeng/api';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {Button} from 'primeng/button';
import {Dialog} from 'primeng/dialog';
import {ProgressSpinner} from 'primeng/progressspinner';
import {InputText} from 'primeng/inputtext';
import {Tooltip} from 'primeng/tooltip';
import {SelectButton} from 'primeng/selectbutton';

import {LabeledComponent} from '../../shared/components/labeled/labeled.component';
import {CopyButtonComponent} from '../../shared/components/copy-button/copy-button.component';
import {EmailDownloadService} from '../../services/email-download.service';
import {SubmitButtonComponent} from '../../shared/components/submit-button/submit-button.component';
import {TypedTableRowDirective} from '../../shared/typed-table-row.directive';
import {DATE_TIME_FORMAT, DATE_FORMAT} from '../../shared/formats';
import {userDisplayName} from '../../shared/user-display-name';
import {describeHttpError, httpErrorFrom, silentErrors} from '../../shared/http-context';
import {
  applicationStatusToTag,
  failureCountToTag,
  homeyStatusToTag,
  lastSyncStatusToTag,
  loginStatusToTag,
  nycProfileStatusToTag,
  paymentStatusToTag,
  Severity,
  subscriptionStatusToTag,
} from '../../shared/enum-to-tag';

import {UsersClient} from '../../api-client/api/api';
import {ActiveUserDetailDto} from '../../api-client/model/active-user-detail-dto';
import {DeletedUserDto} from '../../api-client/model/deleted-user-dto';
import {UserDetailDto} from '../../api-client/model/user-detail-dto';
import {HousingConnectCredentialsDto} from '../../api-client/model/housing-connect-credentials-dto';
import {ReRegisterUserCommandDto} from '../../api-client/model/re-register-user-command-dto';
import {MarkUserAsPaidCommandDto} from '../../api-client/model/mark-user-as-paid-command-dto';
import {RegistrationProgressDto} from '../../api-client/model/registration-progress-dto';
import {IgnoredReason} from '../../api-client/model/ignored-reason';
import {ConnectedAccountProvider} from '../../api-client/model/connected-account-provider';
import {HouseholdIncomeTypeDto} from '../../api-client/model/household-income-type-dto';

type SyncProcessingTag = {
  readonly label: string;
  readonly severity: Severity;
};

const NO_HC_PROFILE = 'no Housing Connect profile';

const INCOME_TYPE_LABELS: Record<HouseholdIncomeTypeDto, string> = {
  employment: 'Employment',
  selfEmployment: 'Self-employment',
  governmentBenefits: 'Government benefits',
  retirementDisability: 'Retirement / disability',
  other: 'Other',
};

/** Matches the toast container in app.component.html that this flow reports into. */
const PROGRESS_TOAST_KEY = 'registration-progress';

const POLL_INTERVAL_MS = 3000;

/**
 * The worker waits ten to twenty seconds before contacting NYC and runs at most two at a time, so a
 * queued attempt can sit for a while behind others. Past this the poll stops and says so, rather
 * than implying an answer it never got: the attempt itself keeps running either way.
 */
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * A queued attempt is over once the profile leaves `not-created` (NYC accepted the registration and
 * the first verification email is now expected) or the auto-create run reports a failure. Anything
 * else -- including a poll that could not be read -- means it is still in flight.
 */
function isRegistrationSettled(progress: RegistrationProgressDto | undefined): boolean {
  if (progress === undefined) {
    return false;
  }
  return progress.status !== 'not-created' || progress.autoCreateStatus === 'failed';
}

@Component({
  selector: 'homey-user-page',
  standalone: true,
  imports: [
    CurrencyPipe,
    DatePipe,
    JsonPipe,
    PrimeTemplate,
    TableModule,
    Tag,
    Button,
    Dialog,
    ProgressSpinner,
    FormsModule,
    InputText,
    Tooltip,
    SelectButton,
    LabeledComponent,
    CopyButtonComponent,
    SubmitButtonComponent,
    TypedTableRowDirective,
  ],
  templateUrl: './user-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPageComponent {
  public readonly userId = input.required<string>();

  private readonly usersClient = inject(UsersClient);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly registerInNycTrigger = viewChild('registerInNycTrigger', {read: ElementRef});
  private readonly reRegisterTrigger = viewChild('reRegisterTrigger', {read: ElementRef});
  private readonly markAsPaidTrigger = viewChild('markAsPaidTrigger', {read: ElementRef});
  private readonly actionRail = viewChild('actionRail', {read: ElementRef});

  // rxResource lets Angular own the subscription, so a superseded or aborted load is unsubscribed
  // instead of running to completion.
  protected readonly user = rxResource<UserDetailDto, {id: string}>({
    params: () => ({id: this.userId()}),
    // silentErrors: a failed load renders as the full-page panel below, with the id and a retry
    // button. The interceptor's toast would say the same thing again, less usefully.
    stream: ({params}) => this.usersClient.getUser(params.id, 'body', false, {context: silentErrors()}),
  });

  // Hitting the daily cap on how many different users one operator may open is a 429 on this very
  // load, so this panel is the only place it can be explained. The raw resource error is no use
  // here -- see httpErrorFrom -- and it is the sentence naming the limit and the retry time that
  // the operator actually needs.
  private readonly loadHttpError = computed<HttpErrorResponse | undefined>(() => httpErrorFrom(this.user.error()));

  protected readonly loadErrorMessage = computed<string>(() => {
    const error = this.loadHttpError();
    return error ? describeHttpError(error) : 'Loading the user failed.';
  });

  protected readonly loadRateLimited = computed<boolean>(() => this.loadHttpError()?.status === 429);

  // `user.value()` THROWS a ResourceValueError while the resource is in its error state
  // (RESOURCE_VALUE_THROWS_ERRORS_DEFAULT is true), so it must never be read without a
  // `hasValue()` guard -- `hasValue()` is explicitly written not to throw.
  private readonly loadedUser = computed<UserDetailDto | undefined>(() =>
    this.user.hasValue() ? this.user.value() : undefined
  );

  protected readonly activeUser = computed<ActiveUserDetailDto | undefined>(() => {
    const value = this.loadedUser();
    return value && value.type === 'user' ? value : undefined;
  });

  protected readonly deletedUser = computed<DeletedUserDto | undefined>(() => {
    const value = this.loadedUser();
    return value && value.type === 'deleted' ? value : undefined;
  });

  // The id stays on the page as copyable text, but it makes a poor heading: operators arrive here
  // from a search and recognise the person, not the uuid.
  protected readonly displayName = computed<string>(() => {
    const user = this.activeUser();
    return user ? userDisplayName(user) : '';
  });

  protected readonly dateTimeFormat = DATE_TIME_FORMAT;
  protected readonly dateFormat = DATE_FORMAT;
  protected readonly incomeTypeLabels = INCOME_TYPE_LABELS;

  protected readonly subscriptionStatusToTag = subscriptionStatusToTag;
  protected readonly paymentStatusToTag = paymentStatusToTag;
  protected readonly nycProfileStatusToTag = nycProfileStatusToTag;
  protected readonly lastSyncStatusToTag = lastSyncStatusToTag;
  protected readonly loginStatusToTag = loginStatusToTag;
  protected readonly applicationStatusToTag = applicationStatusToTag;
  protected readonly homeyStatusToTag = homeyStatusToTag;
  protected readonly failureCountToTag = failureCountToTag;

  protected readonly tableScrollHeight = '360px';

  private readonly emailDownloadService = inject(EmailDownloadService);
  protected readonly downloadingEmailId = this.emailDownloadService.downloadingEmailId;

  protected downloadEmail(emailId: string): void {
    this.emailDownloadService.download(emailId);
  }

  protected readonly syncProcessingTag = computed<SyncProcessingTag | undefined>(() => {
    const state = this.activeUser()?.syncProcessingState;

    if (!state) {
      return undefined;
    }

    if (state.locked) {
      return {label: 'Syncing', severity: 'warn'};
    }

    if (state.priorityQueued) {
      return {label: 'Prio sync scheduled', severity: 'info'};
    }

    if (state.scheduledQueued) {
      return {label: 'Low prio sync scheduled', severity: 'info'};
    }

    return undefined;
  });

  protected readonly registerInNycVisible = signal(false);

  protected readonly nycAuth = computed(() => {
    const profile = this.activeUser()?.housingConnectProfile;
    return this.registerInNycVisible() && profile ? profile : undefined;
  });

  // Credentials are not part of the loaded user -- see HousingConnectProfileDto on the backend --
  // so they exist client-side only after a deliberate reveal. Keyed on `userId` so navigating to
  // another user resets to hidden: without that, one reveal would silently uncover the
  // credentials of every user visited afterwards.
  protected readonly revealedCredentials = linkedSignal<string, HousingConnectCredentialsDto | undefined>({
    source: this.userId,
    computation: () => undefined,
  });

  // Reported next to the Reveal button rather than as a toast: hitting the daily quota is an
  // answer to what the operator just asked for, and it has to stay readable while they decide what
  // to do, not slide away after eight seconds.
  protected readonly credentialsError = linkedSignal<string, string | undefined>({
    source: this.userId,
    computation: () => undefined,
  });

  protected readonly revealCredentials = async (): Promise<void> => {
    this.credentialsError.set(undefined);

    try {
      // silentErrors: the message is rendered in place below, so the interceptor's generic
      // "Request failed - 429" toast would only repeat it, worse.
      const credentials = await firstValueFrom(
        this.usersClient.revealHousingConnectCredentials(this.userId(), 'body', false, {context: silentErrors()})
      );
      this.revealedCredentials.set(credentials);
    } catch (error: unknown) {
      this.credentialsError.set(
        error instanceof HttpErrorResponse ? describeHttpError(error) : 'Could not reveal the credentials.'
      );
    }
  };

  protected hideCredentials(): void {
    this.revealedCredentials.set(undefined);
    this.credentialsError.set(undefined);
  }

  protected readonly registerInNycDisabled = computed(
    () => this.activeUser()?.housingConnectProfile?.status !== 'not-created'
  );

  protected readonly registerInNycTooltip = computed<string>(() => {
    const status = this.activeUser()?.housingConnectProfile?.status ?? NO_HC_PROFILE;
    return `Only available while the Housing Connect profile has not been created yet - currently: ${status}`;
  });

  protected readonly sendTextMessageDisabled = computed<boolean>(() => {
    const user = this.activeUser();
    return !user || !user.phone || !user.smsNotificationsEnabled;
  });

  protected readonly sendTextMessageTooltip = computed<string>(() => {
    const user = this.activeUser();

    if (!user || !user.phone) {
      return 'No phone number on file for this user, so no text message can be sent.';
    }

    if (!user.smsNotificationsEnabled) {
      return 'This user opted out of SMS notifications, so no text message can be sent.';
    }

    return '';
  });

  protected reloadUser(): void {
    this.user.reload();
  }

  protected registerInNyc(): void {
    this.registerInNycVisible.set(true);
  }

  protected closeRegisterInNycDialog(): void {
    this.registerInNycVisible.set(false);
  }

  // PrimeNG's Dialog traps focus while open but never restores it on close, so every exit path
  // (Escape, Cancel, Mark as registered) would otherwise drop focus onto <body>. `onHide` fires
  // after the container is destroyed, which is the only point where focusing the opener sticks.
  protected restoreRegisterInNycFocus(): void {
    this.restoreFocusTo(this.registerInNycTrigger());
  }

  /**
   * Focuses the button inside `triggerHost`, or the rail itself when that button is gone or disabled
   * -- which is the usual case after confirming, since the action tends to disable its own opener --
   * rather than dropping the keyboard user back at the top of the document.
   */
  private restoreFocusTo(triggerHost: ElementRef<HTMLElement> | undefined): void {
    const trigger = triggerHost?.nativeElement.querySelector('button');

    if (trigger && !trigger.disabled) {
      trigger.focus();
      return;
    }

    const rail: HTMLElement | undefined = this.actionRail()?.nativeElement;
    rail?.focus();
  }

  protected readonly confirmRegisteredInNyc = async (): Promise<void> => {
    const user = this.activeUser();
    if (!user) {
      return;
    }

    try {
      await firstValueFrom(this.usersClient.markUserAsRegisteredInNyc(user.id));
      // Await a fresh read before reporting success: `reload()` is fire-and-forget and the
      // resource keeps serving the stale value meanwhile, which would show a green toast next to
      // an unchanged status and a re-armed button.
      this.user.set(await firstValueFrom(this.usersClient.getUser(user.id)));
      this.messageService.add({
        severity: 'success',
        summary: 'Registered in NYC',
        detail: `User ${user.email} marked as registered in NYC`,
      });
    } finally {
      this.registerInNycVisible.set(false);
    }
  };

  protected readonly reRegisterVisible = signal(false);
  protected readonly reRegisterEmail1 = signal('');
  protected readonly reRegisterEmail2 = signal('');
  protected readonly reRegisterUsername = signal('');

  // Rendered inside the dialog rather than as a toast: every way this call can be refused names a
  // value the operator just typed ("that address belongs to someone else", "the two must differ"),
  // so it has to stay on screen next to the field they need to change.
  protected readonly reRegisterError = signal<string | undefined>(undefined);

  /** True from the moment the attempt is queued until the poll below reaches a verdict. */
  protected readonly reRegistering = signal(false);

  protected readonly reRegisterDisabled = computed(() => {
    const status = this.activeUser()?.housingConnectProfile?.status;
    return status === undefined || status === 'complete' || this.reRegistering();
  });

  protected readonly reRegisterTooltip = computed<string>(() => {
    if (this.reRegistering()) {
      return 'A re-registration for this user is already running.';
    }

    const status = this.activeUser()?.housingConnectProfile?.status ?? NO_HC_PROFILE;
    return status === 'complete'
      ? 'This user is already fully registered in NYC - re-registering would discard the live account.'
      : `No Housing Connect profile to re-register - currently: ${status}`;
  });

  /**
   * True once NYC has an account under the current identity -- everything past `not-created` is
   * reached only after a registration succeeded. Re-using that username or first address would just
   * earn a duplicate rejection, so the backend insists on new ones and the dialog says so upfront
   * rather than letting the operator find out by pressing the button.
   */
  protected readonly reRegisterNeedsNewIdentity = computed<boolean>(() => {
    const status = this.activeUser()?.housingConnectProfile?.status;
    return status !== undefined && status !== 'not-created';
  });

  protected openReRegister(): void {
    const profile = this.activeUser()?.housingConnectProfile;
    if (!profile) {
      return;
    }

    this.reRegisterError.set(undefined);
    this.reRegisterEmail1.set(profile.email1);
    this.reRegisterEmail2.set(profile.email2);
    // Left blank on purpose, and read by the backend as "keep the stored one". The current username
    // is not part of the user response -- it only arrives through the audited, daily-capped reveal
    // -- so prefilling it would spend one of the daily reveals to display a value that is usually
    // not the thing being changed.
    this.reRegisterUsername.set('');
    this.reRegisterVisible.set(true);
  }

  protected closeReRegisterDialog(): void {
    this.reRegisterVisible.set(false);
  }

  // Same reason as the Register in NYC dialog: PrimeNG traps focus while open and never gives it
  // back, so every exit path would leave the keyboard on <body>.
  protected restoreReRegisterFocus(): void {
    this.restoreFocusTo(this.reRegisterTrigger());
  }

  protected readonly submitReRegister = async (): Promise<void> => {
    const user = this.activeUser();
    if (!user) {
      return;
    }

    this.reRegisterError.set(undefined);

    const command: ReRegisterUserCommandDto = {
      email1: this.reRegisterEmail1().trim(),
      email2: this.reRegisterEmail2().trim(),
      username: this.reRegisterUsername().trim() || undefined,
    };

    try {
      // silentErrors: a rejected address or an already-complete profile is an answer to what was
      // just typed, shown in the dialog. The interceptor's "Request failed - 409" would bury it.
      await firstValueFrom(
        this.usersClient.reRegisterUser(user.id, command, 'body', false, {context: silentErrors()})
      );
    } catch (error: unknown) {
      this.reRegisterError.set(
        error instanceof HttpErrorResponse ? describeHttpError(error) : 'Could not re-register this user.'
      );
      return;
    }

    this.reRegisterVisible.set(false);
    this.reRegistering.set(true);
    this.showProgress('info', 'Re-registering', `Queued a new NYC registration for ${command.email1}.`, true);
    this.pollRegistration(user.id, command.email1);
  };

  /**
   * Follows the queued attempt to a verdict.
   *
   * Polling rather than awaiting a response, because the work does not happen in the request: the
   * route hands the user to the same FIFO queue the scheduler feeds, and the worker deliberately
   * waits ten to twenty seconds before touching NYC. A single failed poll is ignored rather than
   * ending the run -- the attempt is still going, and a blip should not report it as unknown.
   */
  private pollRegistration(userId: string, email1: string): void {
    const startedAt = Date.now();

    // Leaving the user page entirely (unlike moving between two users) destroys this component and
    // takes the poll with it. Because `takeUntilDestroyed` sits after `last()`, that completes the
    // stream without emitting -- no verdict, and a sticky toast that would otherwise sit there
    // claiming to be working on something nothing is watching any more.
    this.destroyRef.onDestroy(() => {
      if (this.reRegistering()) {
        this.showProgress(
          'warn',
          'Re-registration still running',
          `The attempt for ${email1} is still queued. Open the user again to see how it ended.`,
          false
        );
      }
    });

    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.usersClient
            .getRegistrationProgress(userId, 'body', false, {context: silentErrors()})
            .pipe(catchError(() => of(undefined)))
        ),
        // Inclusive, so the emission that ended the run is the one `last()` reports on.
        takeWhile(
          progress => !isRegistrationSettled(progress) && Date.now() - startedAt < POLL_TIMEOUT_MS,
          true
        ),
        last(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: progress => {
          void this.finishReRegistration(userId, email1, progress);
        },
        error: () => {
          this.reRegistering.set(false);
          this.showProgress(
            'warn',
            'Re-registration status unknown',
            `The attempt for ${email1} was queued, but its progress could not be read. Reload the page to see where it ended up.`,
            false
          );
        },
      });
  }

  private async finishReRegistration(
    userId: string,
    email1: string,
    progress: RegistrationProgressDto | undefined
  ): Promise<void> {
    this.reRegistering.set(false);

    if (progress === undefined || !isRegistrationSettled(progress)) {
      this.showProgress(
        'warn',
        'Re-registration still running',
        `NYC has not answered for ${email1} yet. It is still queued - reload the page in a few minutes.`,
        false
      );
      return;
    }

    if (progress.autoCreateStatus === 'failed') {
      this.showProgress(
        'error',
        'Re-registration failed',
        progress.autoCreateFailureReason ?? `NYC rejected the registration for ${email1}.`,
        false
      );
    } else {
      this.showProgress(
        'success',
        'Re-registered',
        `${email1} was accepted by NYC - now waiting on the first verification email.`,
        false
      );
    }

    // `users/:userId` is a single route with a single component, so moving between two users
    // re-uses this instance and only changes the input -- the poll survives it. Writing this
    // response into the resource would then replace whoever the operator moved on to.
    if (this.userId() !== userId) {
      return;
    }

    try {
      // silentErrors is deliberately not used: this refresh is nobody's explicit request, so the
      // interceptor's toast is the right place for it to fail. Caught all the same -- the caller
      // is a `void`ed promise, and a rejection here has nowhere to land.
      this.user.set(await firstValueFrom(this.usersClient.getUser(userId)));
    } catch {
      // The verdict toast above already stands; the page is just showing pre-attempt data.
    }
  }

  /**
   * Replaces whatever this flow last said, in its own toast group -- see app.component.html. One
   * message that changes as the attempt moves along, rather than a pile of them.
   */
  private showProgress(severity: 'info' | 'success' | 'warn' | 'error', summary: string, detail: string, sticky: boolean): void {
    this.messageService.clear(PROGRESS_TOAST_KEY);
    this.messageService.add({
      key: PROGRESS_TOAST_KEY,
      severity,
      summary,
      detail,
      sticky,
      life: sticky ? undefined : 10000,
    });
  }

  protected readonly markAsPaidVisible = signal(false);
  protected readonly markAsPaidType = signal<MarkUserAsPaidCommandDto.TypeEnum>('stripe');
  protected readonly markAsPaidTypeOptions: {label: string; value: MarkUserAsPaidCommandDto.TypeEnum}[] = [
    {label: 'Stripe', value: 'stripe'},
    {label: 'RevenueCat', value: 'revenue-cat'},
  ];

  protected readonly markAsPaidDisabled = computed<boolean>(
    () => this.activeUser()?.subscription?.paymentStatus === 'paid',
  );

  protected openMarkAsPaid(): void {
    this.markAsPaidType.set('stripe');
    this.markAsPaidVisible.set(true);
  }

  protected closeMarkAsPaidDialog(): void {
    this.markAsPaidVisible.set(false);
  }

  protected restoreMarkAsPaidFocus(): void {
    this.restoreFocusTo(this.markAsPaidTrigger());
  }

  protected readonly confirmMarkAsPaid = async (): Promise<void> => {
    const user = this.activeUser();
    if (!user) {
      return;
    }

    try {
      await firstValueFrom(this.usersClient.markUserAsPaid(user.id, {type: this.markAsPaidType()}));
      // Awaited for the same reason as `confirmRegisteredInNyc`: a fire-and-forget reload would
      // show the success toast next to a still-unpaid subscription panel.
      this.user.set(await firstValueFrom(this.usersClient.getUser(user.id)));
      this.messageService.add({
        severity: 'success',
        summary: 'Marked as paid',
        detail: `User ${user.email} marked as paid`,
      });
    } finally {
      this.markAsPaidVisible.set(false);
    }
  };

  protected goToSendTextMessage(): void {
    this.router.navigate(['/users', this.userId(), 'send-text-message']);
  }

  protected confirmDelete(): void {
    const user = this.activeUser();
    if (!user) {
      return;
    }

    this.confirmationService.confirm({
      message: `Permanently delete ${user.email} (${user.id})? This deletes the user and everything attached to it. This action cannot be undone.`,
      header: 'Delete user',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete user',
      rejectLabel: 'Cancel',
      acceptButtonProps: {label: 'Delete user', severity: 'danger', icon: 'pi pi-trash'},
      rejectButtonProps: {label: 'Cancel', severity: 'secondary', text: true},
      accept: () => {
        // silentErrors: a refused delete needs to say *why* -- the daily quota, and when it frees
        // up again -- which the interceptor's generic toast buries behind "Request failed".
        this.usersClient
          .deleteUser(user.id, 'body', false, {context: silentErrors()})
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'User deleted',
                detail: `User ${user.email} deleted`,
              });
              this.router.navigate(['/users']);
            },
            error: (error: unknown) => {
              // A quota refusal is not a malfunction: nothing was deleted, and the operator can
              // act on it (wait, or hand it to a colleague). Warn, and hold it on screen longer
              // than a normal toast so the reset time can actually be read.
              const rateLimited = error instanceof HttpErrorResponse && error.status === 429;
              this.messageService.add({
                severity: rateLimited ? 'warn' : 'error',
                summary: rateLimited ? 'Daily delete limit reached' : 'Delete failed',
                detail:
                  error instanceof HttpErrorResponse
                    ? describeHttpError(error)
                    : `Could not delete user ${user.email}.`,
                life: rateLimited ? 15000 : 8000,
              });
            },
          });
      },
    });
  }

  protected ignoredReasonLabel(reason: IgnoredReason): string {
    switch (reason) {
      case 'prevent-sync':
        return 'Prevent sync';
      case 'incorrect-username-password':
        return 'Incorrect username/password';
      case 'unknown':
      default:
        return 'Unknown';
    }
  }

  protected ignoredReasonSeverity(reason: IgnoredReason): Severity {
    switch (reason) {
      case 'prevent-sync':
        return 'warn';
      case 'incorrect-username-password':
        return 'danger';
      case 'unknown':
      default:
        return 'info';
    }
  }

  protected connectedAccountSeverity(provider: ConnectedAccountProvider): Severity {
    return provider === 'cognito' ? 'success' : 'info';
  }

  protected connectedAccountProviderLabel(provider: ConnectedAccountProvider): string {
    switch (provider) {
      case 'google':
        return 'Google (federated)';
      case 'apple':
        return 'Apple (federated)';
      default:
        return 'Homey login';
    }
  }

}
